import { supabase, tenantFrom, subscribeToChannel } from "./supabaseClient";

export const salesRepository = {
  list: (tenantId: string) =>
    supabase
      .from("sales")
      .select(
        `
        *,
        customer:customers(id, name, email, whatsapp, instagram, document, cep, street, number, complement, neighborhood, city, state),
        sale_items(*),
        checkout_sessions(id, status, expires_at, created_at, updated_at, delivery_method, customer_data, shipping_address, shipping_cost, shipping_service, pickup_address_id, items, pickup_store:store_addresses(id, name, street, number, complement, neighborhood, city, state, cep), checkout_payments(id, status, payment_method, paid_at, created_at, metadata))
      `
      )
      .eq("tenant_id", tenantId)
      .order("created_at", { ascending: false })
      .limit(500),

  listPaginated: async (tenantId: string, opts: { page: number; pageSize: number; fulfillmentStatus?: string; channels?: string[]; search?: boolean; searchTerm?: string; dateFrom?: string; dateTo?: string; productIds?: string[]; paymentStatuses?: string[]; deliveryMethods?: string[] }) => {
    const { page, pageSize, fulfillmentStatus, channels, search, searchTerm, dateFrom, dateTo, productIds, paymentStatuses, deliveryMethods } = opts;
    const saleItemFields = "product_id, product_name, variation_id, variation_description, quantity, unit_price, total_price";
    const saleItemsJoin = productIds && productIds.length > 0 ? `sale_items!inner(${saleItemFields})` : `sale_items(${saleItemFields})`;
    const selectFieldsLight = `id, created_at, total, subtotal, payment_status, payment_method,
        fulfillment_status, sales_channel, delivery_method, notes, customer_id, discount, tenant_id, stock_deducted, bag_id,
        customer:customers(id, name, instagram, cep, street, number, complement, neighborhood, city, state),
        checkout_sessions(id, status, expires_at, created_at, updated_at, customer_id, customer_data, shipping_address, pickup_address_id, checkout_payments(status)),
        ${saleItemsJoin}`;

    const term = searchTerm?.trim();
    if (search && term && term.length >= 2) {
      try {
        const { data: idRows, error: rpcError } = await supabase.rpc("search_tenant_sale_ids", {
          p_tenant_id: tenantId,
          p_term: term,
          p_limit: 200,
        });
        if (!rpcError && Array.isArray(idRows)) {
          const ids = (idRows as Array<{ id: string }>).map((r) => r.id);
          if (ids.length === 0) {
            return { data: [], count: 0, error: null };
          }
          let q = supabase
            .from("sales")
            .select(selectFieldsLight, { count: "estimated" })
            .eq("tenant_id", tenantId)
            .in("id", ids)
            .order("created_at", { ascending: false });
          if (productIds && productIds.length > 0) {
            q = q.in("sale_items.product_id", productIds);
          }
          if (fulfillmentStatus && fulfillmentStatus !== "all") {
            if (fulfillmentStatus === "to_separate") {
              q = q.eq("payment_status", "paid").or("fulfillment_status.is.null,fulfillment_status.eq.pending");
            } else if (fulfillmentStatus === "delivered") {
              q = q.or("fulfillment_status.eq.delivered,fulfillment_status.eq.shipped");
            } else {
              q = q.eq("fulfillment_status", fulfillmentStatus);
            }
          }
          if (channels && channels.length > 0) {
            q = q.in("sales_channel", channels);
          }
          if (paymentStatuses && paymentStatuses.length > 0) {
            q = q.in("payment_status", paymentStatuses);
          }
          if (deliveryMethods && deliveryMethods.length > 0) {
            q = q.in("delivery_method", deliveryMethods);
          }
          return q;
        }
      } catch (e) {
        console.warn("search_tenant_sale_ids RPC threw, falling back to client-side search:", e);
      }
    }

    let query = supabase
      .from("sales")
      .select(selectFieldsLight, { count: "estimated" })
      .eq("tenant_id", tenantId)
      .order("created_at", { ascending: false });

    if (!search && dateFrom) {
      query = query.gte("created_at", dateFrom);
    }
    if (!search && dateTo) {
      query = query.lte("created_at", dateTo);
    }

    if (productIds && productIds.length > 0) {
      query = query.in("sale_items.product_id", productIds);
    }

    if (fulfillmentStatus && fulfillmentStatus !== "all") {
      if (fulfillmentStatus === "to_separate") {
        query = query.eq("payment_status", "paid").or("fulfillment_status.is.null,fulfillment_status.eq.pending");
      } else if (fulfillmentStatus === "delivered") {
        query = query.or("fulfillment_status.eq.delivered,fulfillment_status.eq.shipped");
      } else {
        query = query.eq("fulfillment_status", fulfillmentStatus);
      }
    }
    if (channels && channels.length > 0) {
      query = query.in("sales_channel", channels);
    }
    if (paymentStatuses && paymentStatuses.length > 0) {
      query = query.in("payment_status", paymentStatuses);
    }
    if (deliveryMethods && deliveryMethods.length > 0) {
      query = query.in("delivery_method", deliveryMethods);
    }

    if (search) {
      return query.limit(1000);
    }
    return query.range(page * pageSize, (page + 1) * pageSize - 1);
  },

  getMetrics: async (tenantId: string, dateFrom?: string, dateTo?: string) => {
    const { data, error } = await supabase.rpc("get_sales_metrics", {
      p_tenant_id: tenantId,
      p_from: dateFrom ?? null,
      p_to: dateTo ?? null,
    });
    if (error) return { data: null, error };
    const row = Array.isArray(data) ? data[0] : data;
    return { data: row as { count_all: number; count_to_separate: number; count_ready: number; count_delivered: number; paid_count: number; revenue: number; total_revenue: number } | null, error: null };
  },

  countByStatus: (tenantId: string, dateFrom?: string, dateTo?: string) => {
    const base = (q: ReturnType<typeof supabase.from>) => {
      const selected = q.select("*", { count: "exact", head: true }).eq("tenant_id", tenantId);
      const withFrom = dateFrom ? selected.gte("created_at", dateFrom) : selected;
      return dateTo ? withFrom.lte("created_at", dateTo) : withFrom;
    };
    return Promise.all([
      base(supabase.from("sales")),
      base(supabase.from("sales"))
        .eq("payment_status", "paid")
        .or("fulfillment_status.is.null,fulfillment_status.eq.pending"),
      base(supabase.from("sales"))
        .eq("fulfillment_status", "ready"),
      base(supabase.from("sales"))
        .or("fulfillment_status.eq.delivered,fulfillment_status.eq.shipped"),
    ]);
  },

  revenueMetrics: (tenantId: string, dateFrom?: string, dateTo?: string) => {
    const base = supabase
      .from("sales")
      .select("total", { count: "exact" })
      .eq("tenant_id", tenantId)
      .eq("payment_status", "paid");
    const withFrom = dateFrom ? base.gte("created_at", dateFrom) : base;
    return dateTo ? withFrom.lte("created_at", dateTo) : withFrom;
  },

  totalRevenueMetrics: (tenantId: string, dateFrom?: string, dateTo?: string) => {
    const base = supabase
      .from("sales")
      .select("total")
      .eq("tenant_id", tenantId)
      .in("payment_status", ["paid", "pending"]);
    const withFrom = dateFrom ? base.gte("created_at", dateFrom) : base;
    return dateTo ? withFrom.lte("created_at", dateTo) : withFrom;
  },

  getById: (tenantId: string, saleId: string) =>
    supabase
      .from("sales")
      .select(
        `
        *,
        customer:customers(id, name, email, whatsapp, instagram, document, cep, street, number, complement, neighborhood, city, state),
        sale_items(*),
        checkout_sessions(id, status, expires_at, created_at, updated_at, delivery_method, customer_data, shipping_address, shipping_cost, shipping_service, pickup_address_id, items, pickup_store:store_addresses(id, name, street, number, complement, neighborhood, city, state, cep), checkout_payments(id, status, payment_method, paid_at, created_at, metadata))
      `
      )
      .eq("tenant_id", tenantId)
      .eq("id", saleId)
      .single(),

  listByCustomer: (tenantId: string, customerId: string) =>
    supabase
      .from("sales")
      .select(
        "id, total, created_at, payment_status, payment_method, delivery_method"
      )
      .eq("customer_id", customerId)
      .eq("tenant_id", tenantId)
      .order("created_at", { ascending: false }),

  create: (tenantId: string, data: Record<string, unknown>) =>
    supabase
      .from("sales")
      .insert({ ...data, tenant_id: tenantId })
      .select()
      .single(),

  update: (tenantId: string, id: string, data: Record<string, unknown>) =>
    supabase.from("sales").update(data).eq("tenant_id", tenantId).eq("id", id),

  updatePaymentStatus: (
    tenantId: string,
    id: string,
    status: string,
    currentStatus = "pending"
  ) =>
    supabase
      .from("sales")
      .update({ payment_status: status })
      .eq("tenant_id", tenantId)
      .eq("id", id)
      .eq("payment_status", currentStatus),

  updateFulfillmentStatus: (tenantId: string, id: string, status: string) =>
    supabase.from("sales").update({ fulfillment_status: status }).eq("tenant_id", tenantId).eq("id", id),

  delete: (tenantId: string, id: string) => supabase.from("sales").delete().eq("tenant_id", tenantId).eq("id", id),

  subscribe: (tenantId: string, callback: () => void) =>
    subscribeToChannel(
      `sales-realtime-${tenantId}`,
      "sales",
      `tenant_id=eq.${tenantId}`,
      ["INSERT", "UPDATE", "DELETE"],
      callback
    ),

  subscribeCheckoutSessions: (tenantId: string, callback: () => void) =>
    subscribeToChannel(
      `checkout-sessions-realtime-${tenantId}`,
      "checkout_sessions",
      `tenant_id=eq.${tenantId}`,
      ["INSERT", "UPDATE", "DELETE"],
      callback
    ),
};

export const saleItemsRepository = {
  create: (items: Record<string, unknown>[]) =>
    supabase.from("sale_items").insert(items),

  deleteForSale: (_tenantId: string, saleId: string) =>
    supabase.from("sale_items").delete().eq("sale_id", saleId),
};

export const saleEventsRepository = {
  list: (saleId: string, tenantId: string) =>
    supabase
      .from("sale_events")
      .select("id, sale_id, event_type, message, created_at")
      .eq("sale_id", saleId)
      .eq("tenant_id", tenantId)
      .order("created_at", { ascending: true })
      .limit(200),

  create: (data: {
    tenant_id: string;
    sale_id: string;
    event_type: string;
    message: string;
  }) => supabase.from("sale_events").insert(data),
};

export const checkoutSessionsRepository = {
  findBySaleId: async (tenantId: string, saleId: string) => {
    const { data, error } = await supabase
      .from("checkout_sessions")
      .select("id, status, shipping_cost, metadata")
      .eq("tenant_id", tenantId)
      .eq("sale_id", saleId)
      .order("created_at", { ascending: false })
      .limit(1);
    return { data: data?.[0] ?? null, error };
  },

  update: (tenantId: string, id: string, data: Record<string, unknown>) =>
    supabase.from("checkout_sessions").update(data).eq("tenant_id", tenantId).eq("id", id),

  updateStatus: (tenantId: string, id: string, status: string, currentStatus = "pending") =>
    supabase
      .from("checkout_sessions")
      .update({ status })
      .eq("tenant_id", tenantId)
      .eq("id", id)
      .eq("status", currentStatus),

  subscribe: (sessionId: string, callback: (payload: unknown) => void) =>
    subscribeToChannel(
      `payment-${sessionId}`,
      "checkout_sessions",
      `id=eq.${sessionId}`,
      ["UPDATE"],
      () => callback(null)
    ),
};

export const salesChartRepository = {
  listPaidSince: (tenantId: string, since: string) =>
    supabase
      .from("sales")
      .select("total, created_at")
      .eq("tenant_id", tenantId)
      .eq("payment_status", "paid")
      .gte("created_at", since),

  listPaidRange: (tenantId: string, dateFrom?: string, dateTo?: string) => {
    const base = supabase
      .from("sales")
      .select("total, created_at")
      .eq("tenant_id", tenantId)
      .eq("payment_status", "paid");
    const withFrom = dateFrom ? base.gte("created_at", dateFrom) : base;
    return (dateTo ? withFrom.lte("created_at", dateTo) : withFrom).limit(5000);
  },

  channelBreakdown: (tenantId: string, dateFrom?: string, dateTo?: string) => {
    const base = supabase
      .from("sales")
      .select("total, sales_channel")
      .eq("tenant_id", tenantId)
      .eq("payment_status", "paid");
    const withFrom = dateFrom ? base.gte("created_at", dateFrom) : base;
    return (dateTo ? withFrom.lte("created_at", dateTo) : withFrom).limit(5000);
  },

  conversionMetrics: (tenantId: string, dateFrom?: string, dateTo?: string) => {
    const applyRange = (q: ReturnType<typeof supabase.from>) => {
      const selected = q.select("*", { count: "exact", head: true }).eq("tenant_id", tenantId);
      const withFrom = dateFrom ? selected.gte("created_at", dateFrom) : selected;
      return dateTo ? withFrom.lte("created_at", dateTo) : withFrom;
    };
    return Promise.all([
      applyRange(supabase.from("checkout_sessions")),
      applyRange(supabase.from("sales")).eq("payment_status", "paid"),
    ]);
  },
};

export const dashboardRepository = {
  fetchMetrics: (
    tenantId: string,
    thirtyDaysAgo: string,
    sixtyDaysAgo: string
  ) =>
    Promise.all([
      supabase
        .from("sales")
        .select("total, payment_status")
        .eq("tenant_id", tenantId)
        .gte("created_at", thirtyDaysAgo),
      supabase
        .from("sales")
        .select("total, payment_status")
        .eq("tenant_id", tenantId)
        .gte("created_at", sixtyDaysAgo)
        .lt("created_at", thirtyDaysAgo),
      supabase
        .from("checkout_sessions")
        .select("status")
        .eq("tenant_id", tenantId)
        .gte("created_at", thirtyDaysAgo),
      supabase
        .from("checkout_sessions")
        .select("status")
        .eq("tenant_id", tenantId)
        .gte("created_at", sixtyDaysAgo)
        .lt("created_at", thirtyDaysAgo),
      supabase
        .from("automation_logs")
        .select("*", { count: "exact", head: true })
        .eq("tenant_id", tenantId)
        .gte("created_at", thirtyDaysAgo),
      supabase
        .from("automation_logs")
        .select("*", { count: "exact", head: true })
        .eq("tenant_id", tenantId)
        .gte("created_at", sixtyDaysAgo)
        .lt("created_at", thirtyDaysAgo),
    ]),
};

export const conversionFunnelRepository = {
  fetch: (tenantId: string, since: string) =>
    Promise.all([
      supabase
        .from("automation_logs")
        .select("*", { count: "exact", head: true })
        .eq("tenant_id", tenantId)
        .gte("created_at", since),
      supabase
        .from("checkout_sessions")
        .select("*", { count: "exact", head: true })
        .eq("tenant_id", tenantId)
        .gte("created_at", since),
      supabase
        .from("sales")
        .select("*", { count: "exact", head: true })
        .eq("tenant_id", tenantId)
        .eq("payment_status", "paid")
        .gte("created_at", since),
    ]),
};
